/**
 * Custom error classes for better error handling and type safety
 */

/**
 * Base class for all custom API errors
 */
export class APIError extends Error {
    public readonly code: string;
    public readonly statusCode?: number;
    public readonly originalError?: unknown;

    constructor(
        message: string,
        code: string,
        statusCode?: number,
        originalError?: unknown
    ) {
        super(message);
        this.name = 'APIError';
        this.code = code;
        this.statusCode = statusCode;
        this.originalError = originalError;

        // Maintains proper stack trace for where error was thrown
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, APIError);
        }
    }
}

/**
 * Error class for OpenAI API specific errors
 */
export class OpenAIError extends APIError {
    constructor(message: string, code: string = 'OPENAI_ERROR', originalError?: unknown) {
        super(message, code, undefined, originalError);
        this.name = 'OpenAIError';
    }
}

/**
 * Error class for authentication errors
 */
export class AuthError extends Error {
    public readonly code: string;
    public readonly originalError?: unknown;

    constructor(message: string, code: string = 'AUTH_ERROR', originalError?: unknown) {
        super(message);
        this.name = 'AuthError';
        this.code = code;
        this.originalError = originalError;

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AuthError);
        }
    }

    /**
     * Create a user-friendly error message for Firebase auth errors
     */
    static fromFirebaseError(error: { code?: string; message?: string }): AuthError {
        const errorMessages: Record<string, string> = {
            'auth/email-already-in-use': 'This email is already registered.',
            'auth/invalid-email': 'Please enter a valid email address.',
            'auth/operation-not-allowed': 'This sign-in method is not enabled.',
            'auth/weak-password': 'Password should be at least 6 characters.',
            'auth/user-disabled': 'This account has been disabled.',
            'auth/user-not-found': 'No account found with this email.',
            'auth/wrong-password': 'Incorrect password.',
            'auth/invalid-credential': 'Invalid email or password.',
            'auth/too-many-requests': 'Too many attempts. Please try again later.',
            'auth/popup-closed-by-user': 'Sign-in was cancelled.',
            'auth/network-request-failed': 'Network error. Please check your connection.',
        };

        const code = error.code || 'auth/unknown';
        const message = errorMessages[code] || error.message || 'An authentication error occurred.';

        return new AuthError(message, code, error);
    }
}

/**
 * Error class for data validation errors
 */
export class ValidationError extends Error {
    public readonly field?: string;
    public readonly errors: string[];

    constructor(message: string, errors: string[] = [], field?: string) {
        super(message);
        this.name = 'ValidationError';
        this.field = field;
        this.errors = errors;

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ValidationError);
        }
    }
}

/**
 * Error class for storage-related errors
 */
export class StorageError extends Error {
    public readonly operation: 'read' | 'write' | 'delete';

    constructor(message: string, operation: 'read' | 'write' | 'delete') {
        super(message);
        this.name = 'StorageError';
        this.operation = operation;

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, StorageError);
        }
    }
}

/**
 * Type guard to check if an error is an APIError
 */
export function isAPIError(error: unknown): error is APIError {
    return error instanceof APIError;
}

/**
 * Type guard to check if an error is an AuthError
 */
export function isAuthError(error: unknown): error is AuthError {
    return error instanceof AuthError;
}

/**
 * Get a user-friendly error message from any error type
 */
export function getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'An unexpected error occurred.';
}
